import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, EyeIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import SEO from '../components/SEO';
import EnTitle from '../components/EnTitle';
import FlowLines from '../components/FlowLines';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';
const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

// ── Hero ─────────────────────────────────────────────────────────────────────
// Charcoal band with the big blue headline on top and the admin-managed
// slide images below it. The images keep their native 12:5 banner ratio
// (slides can have text baked into the artwork, so they must not be
// cropped to a different shape) and overhang into the white section below.
function Hero({ slides }) {
  const { t, language } = useLanguage();
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const count = slides.length;
  const restart = () => {
    clearInterval(timerRef.current);
    if (count > 1) timerRef.current = setInterval(() => setCurrent(c => (c + 1) % count), 5000);
  };
  const goTo = (i) => { setCurrent(i); restart(); };

  useEffect(() => {
    if (count > 1) timerRef.current = setInterval(() => setCurrent(c => (c + 1) % count), 5000);
    return () => clearInterval(timerRef.current);
  }, [count]);

  const slide = slides[current];
  const title = (slide && ((language === 'ja' && slide.title_ja) || slide.title)) || t('home.heroFallbackTitle');
  const subtitle = (slide && ((language === 'ja' && slide.subtitle_ja) || slide.subtitle)) || t('home.heroFallbackSubtitle');

  return (
    <section className="relative overflow-hidden"
      style={{ background: slides.length ? 'linear-gradient(to bottom, #333 0, #333 72%, #fff 72%)' : '#333' }}>
      <FlowLines className="opacity-80" />
      <div className="relative w-[90%] max-w-[1600px] mx-auto pt-14 lg:pt-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,340px)] gap-8 lg:gap-16 items-end mb-10 lg:mb-14">
          <h1 key={title} className="en-title text-primary fade-up text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] break-words">
            {title}
          </h1>
          <div className="text-white">
            <p className="font-btn text-xs tracking-[0.25em] text-white/60 mb-3">[PRECISION] [MEASUREMENT] [SOLUTIONS]</p>
            <p className="text-sm sm:text-base font-bold leading-relaxed mb-6">{subtitle}</p>
            <Link to="/contact" className="btn-primary btn-arrow text-sm">{t('common.contactUs')}</Link>
          </div>
        </div>

        {slides.length > 0 ? (
          <>
            <div className="relative aspect-[12/5] min-h-[200px] max-h-[720px] overflow-hidden bg-gray-200">
              {slides.map((s, i) => (
                <div key={s.id} className={`absolute inset-0 transition-opacity duration-1000 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
                  {s.image_url && (
                    <img src={imgSrc(s.image_url)} alt={(language === 'ja' && s.title_ja) || s.title || 'Sankyo Machine Tools'}
                      className="w-full h-full object-cover" />
                  )}
                </div>
              ))}
            </div>
            {slides.length > 1 && (
              <div className="flex items-center justify-between py-5">
                <div className="flex gap-2.5">
                  {slides.map((_, i) => (
                    <button key={i} onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`}
                      className={`h-1 transition-all duration-300 ${i === current ? 'w-10 bg-primary' : 'w-5 bg-gray-300 hover:bg-gray-400'}`} />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => goTo((current - 1 + slides.length) % slides.length)} aria-label="Previous slide"
                    className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-charcoal hover:bg-charcoal hover:text-white hover:border-charcoal transition-colors">
                    <ChevronRightIcon className="w-4 h-4 rotate-180" />
                  </button>
                  <button onClick={() => goTo((current + 1) % slides.length)} aria-label="Next slide"
                    className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-charcoal hover:bg-charcoal hover:text-white hover:border-charcoal transition-colors">
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="h-16" />
        )}
      </div>
    </section>
  );
}

// ── News (latest promotional posts) ──────────────────────────────────────────
function NewsSection({ posts }) {
  const { t } = useLanguage();
  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="w-[90%] max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 lg:gap-24">
        <div className="flex flex-col gap-8">
          <EnTitle lines={['NEWS']} sub={t('home.newsSub')} className="text-6xl lg:text-7xl" />
          <Link to="/promotional" className="btn-dark btn-arrow text-sm self-start min-w-[170px] justify-between hidden lg:inline-flex">{t('home.allNews')}</Link>
        </div>
        <ul className="border-t border-gray-200">
          {posts.map(p => (
            <li key={p.id} className="border-b border-gray-200">
              <Link to={`/promotional/${p.slug}`} className="group flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-10 py-6 pr-8 relative">
                <span className="font-btn font-semibold text-sm text-primary shrink-0 w-28">{formatDate(p.published_at)}</span>
                <span className="text-sm sm:text-base text-charcoal group-hover:text-primary transition-colors line-clamp-2">{p.title}</span>
                <span className="absolute right-1 top-1/2 -translate-y-1/2 w-2 h-2 border-t-2 border-r-2 border-gray-400 rotate-45 group-hover:border-primary group-hover:translate-x-1 transition-all" />
              </Link>
            </li>
          ))}
        </ul>
        <Link to="/promotional" className="btn-dark btn-arrow text-sm self-start min-w-[170px] justify-between lg:hidden">{t('home.allNews')}</Link>
      </div>
    </section>
  );
}

// ── About ────────────────────────────────────────────────────────────────────
function AboutSection({ settings }) {
  const { t, language } = useLanguage();
  const aboutTitle = (language === 'ja' && settings.about_title_ja) || settings.about_title || 'Sankyo Machine Tools';
  const aboutContent = (language === 'ja' && settings.about_content_ja) || settings.about_content || '';
  const stats = [
    [settings.stat_years || '70+', 'stat1'],
    [settings.stat_established || '2016', 'stat2'],
    [settings.stat_products || '100+', 'stat3'],
    [settings.stat_clients || '5+', 'stat4'],
  ];
  return (
    <section className="py-16 lg:py-24 bg-[#f5f5f5]">
      <div className="w-[90%] max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <EnTitle lines={['ABOUT US']} blue={[0]} sub={t('home.aboutSub')} className="text-5xl lg:text-7xl" />
          <h3 className="text-2xl sm:text-3xl font-bold text-ink mt-10 mb-6 leading-snug">{aboutTitle}</h3>
          <div className="text-gray-600 leading-loose space-y-4 text-sm sm:text-base">
            {aboutContent.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
          </div>
          <Link to="/company-info" className="btn-primary btn-arrow text-sm mt-10 min-w-[170px] justify-between">{t('home.detail')}</Link>
        </div>
        <div className="grid grid-cols-2 bg-white">
          {stats.map(([n, key], i) => (
            <div key={key} className={`px-6 py-10 sm:py-14 text-center border-gray-200 ${i % 2 === 0 ? 'border-r' : ''} ${i < 2 ? 'border-b' : ''}`}>
              <div className="font-display font-extrabold text-4xl sm:text-5xl text-primary tracking-tight">{n}</div>
              <div className="text-xs sm:text-sm text-charcoal mt-3 font-medium">{t(`home.${key}`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Products ─────────────────────────────────────────────────────────────────
function ProductsSection({ categories }) {
  const { t } = useLanguage();
  return (
    <section className="relative bg-charcoal text-white pt-16 lg:pt-24 pb-10 overflow-hidden">
      <div className="w-[90%] max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-24 items-end mb-12 lg:mb-16">
          <EnTitle lines={['OUR', 'PRODUCTS']} light sub={t('home.productsSub')} className="text-6xl sm:text-7xl lg:text-8xl" />
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-end">
            <p className="text-sm leading-loose text-white/85 max-w-xl">{t('home.productsIntro')}</p>
            <Link to="/products" className="btn-primary btn-arrow text-sm self-start md:self-auto min-w-[190px] justify-between">{t('home.allProducts')}</Link>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
          {categories.slice(0, 8).map(cat => (
            <Link key={cat.id} to={`/products/${cat.slug}`} className="group block">
              <div className="aspect-[4/3] bg-white/10 overflow-hidden relative mb-4">
                {cat.image_url ? (
                  <img src={imgSrc(cat.image_url)} alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="font-display font-extrabold text-5xl text-white/20">{cat.name[0]}</span>
                  </div>
                )}
                {cat.unique_view_count > 0 && (
                  <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-black/50 text-white text-xs font-medium px-2 py-0.5 rounded-full backdrop-blur-sm">
                    <EyeIcon className="w-3 h-3" />
                    {cat.unique_view_count}
                  </div>
                )}
              </div>
              <h3 className="font-bold text-base sm:text-lg leading-snug mb-2 group-hover:text-primary transition-colors">{cat.name}</h3>
              {cat.description && <p className="text-xs sm:text-sm text-white/65 line-clamp-2 leading-relaxed">{cat.description}</p>}
            </Link>
          ))}
        </div>
      </div>
      {/* Scrolling wordmark strip */}
      <div className="mt-16 lg:mt-24 overflow-hidden whitespace-nowrap select-none" aria-hidden="true">
        <div className="inline-flex animate-marquee">
          {Array.from({ length: 2 }).map((_, k) => (
            <span key={k} className="font-display font-extrabold uppercase text-6xl lg:text-8xl tracking-tight text-white/[0.06] pr-10">
              Precision Machinery · Measuring Equipment · Industrial Solutions · Precision Machinery · Measuring Equipment · Industrial Solutions ·&nbsp;
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Clients ──────────────────────────────────────────────────────────────────
function ClientsSection({ clients }) {
  const { t } = useLanguage();
  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="w-[90%] max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10 lg:mb-14">
          <EnTitle lines={['OUR', 'CLIENTS']} sub={t('home.clientsSub')} className="text-5xl sm:text-6xl lg:text-7xl" />
          <Link to="/clients" className="btn-dark btn-arrow text-sm self-start sm:self-auto min-w-[180px] justify-between">{t('home.allClients')}</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-t border-l border-gray-200">
          {clients.map(m => (
            <Link key={m.id} to={`/clients/${m.slug}`}
              className="group border-r border-b border-gray-200 p-6 flex flex-col items-center justify-center gap-3 min-h-[140px] hover:bg-[#f5f5f5] transition-colors">
              {m.logo_url ? (
                <img src={imgSrc(m.logo_url)} alt={m.name} className="h-12 max-w-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300" />
              ) : (
                <span className="font-bold text-charcoal text-sm text-center">{m.name}</span>
              )}
              <span className="text-xs text-gray-500 text-center group-hover:text-primary transition-colors">{m.name}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Recruit ──────────────────────────────────────────────────────────────────
function RecruitSection({ settings }) {
  const { t } = useLanguage();
  return (
    <section className="py-16 lg:py-24 bg-[#f5f5f5]">
      <div className="w-[90%] max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
        <div className="aspect-[4/3] bg-gray-200 overflow-hidden">
          {settings.careers_image_url ? (
            <img src={imgSrc(settings.careers_image_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-charcoal">
              <UserGroupIcon className="w-16 h-16 text-white/20" />
            </div>
          )}
        </div>
        <div>
          <EnTitle lines={['JOIN', 'OUR TEAM']} sub={t('nav.recruit')} className="text-6xl sm:text-7xl lg:text-8xl" />
          <p className="text-base sm:text-lg font-bold text-charcoal mt-8 mb-8 leading-relaxed">{t('home.recruitText')}</p>
          <Link to="/careers" className="btn-dark btn-arrow text-sm min-w-[220px] justify-between">{t('home.recruitButton')}</Link>
        </div>
      </div>
    </section>
  );
}

// ── Main Home Page ────────────────────────────────────────────────────────────
export default function Home() {
  const [slides, setSlides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    Promise.all([
      API.get('/hero'),
      API.get('/categories'),
      API.get('/clients'),
      API.get('/settings'),
    ]).then(([h, c, m, s]) => {
      setSlides(h.data);
      setCategories(c.data);
      setClients(m.data);
      setSettings(s.data);
    }).catch(() => {});
    // Loaded separately so a failure here never blanks the rest of the page.
    API.get('/promotions').then(r => setPosts(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <PageLayout>
      <SEO title="Precision Machinery & Measuring Equipment" description="Sankyo Machine Tools Philippines Inc. — Japanese trading company supplying precision machinery, measuring equipment, and industrial solutions across the Philippines." />
      <Hero slides={slides} />
      {posts.length > 0 && <NewsSection posts={posts} />}
      <AboutSection settings={settings} />
      {categories.length > 0 && <ProductsSection categories={categories} />}
      {clients.length > 0 && <ClientsSection clients={clients} />}
      <RecruitSection settings={settings} />
    </PageLayout>
  );
}
